import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Signup() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    preferred_locations: [],
    desired_home_type: '',
    budget_range: '',
    buying_timeline: '',
    notes: '',
  });

  const handleLocationChange = (location, checked) => {
    setFormData((prev) => ({
      ...prev,
      preferred_locations: checked
        ? [...prev.preferred_locations, location]
        : prev.preferred_locations.filter((l) => l !== location),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - only name, email, phone are required
    if (!formData.full_name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields (Name, Email, Phone)');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/signup`, formData);
      setIsSuccess(true);
      toast.success('Thank you! Your information has been submitted successfully.');
    } catch (error) {
      console.error('Signup error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center" data-testid="success-message">
        <Card className="max-w-2xl w-full mx-4">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="flex justify-center mb-6">
              <CheckCircle2 className="h-20 w-20 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Thank You!
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Your information has been received. I'll review your details and get back to you within 24 hours to discuss your home buying needs.
            </p>
            <div className="space-y-3">
              <Button
                data-testid="success-back-home-btn"
                onClick={() => navigate('/')}
                className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white w-full sm:w-auto px-8"
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card data-testid="signup-form">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-[#1e3a8a]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Start Your Home Search
            </CardTitle>
            <CardDescription className="text-base">
              Fill out this form and I'll contact you within 24 hours to discuss your needs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  data-testid="signup-fullname-input"
                  type="text"
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  data-testid="signup-email-input"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  data-testid="signup-phone-input"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>

              {/* Preferred Locations */}
              <div className="space-y-2">
                <Label>Preferred Location(s)</Label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="california"
                      data-testid="signup-location-california"
                      checked={formData.preferred_locations.includes('California')}
                      onCheckedChange={(checked) => handleLocationChange('California', checked)}
                    />
                    <label htmlFor="california" className="text-sm cursor-pointer">
                      California
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="florida"
                      data-testid="signup-location-florida"
                      checked={formData.preferred_locations.includes('Florida')}
                      onCheckedChange={(checked) => handleLocationChange('Florida', checked)}
                    />
                    <label htmlFor="florida" className="text-sm cursor-pointer">
                      Florida
                    </label>
                  </div>
                </div>
              </div>

              {/* Desired Home Type */}
              <div className="space-y-2">
                <Label htmlFor="home_type">Desired Home Type</Label>
                <Select
                  value={formData.desired_home_type}
                  onValueChange={(value) => setFormData({ ...formData, desired_home_type: value })}
                >
                  <SelectTrigger data-testid="signup-hometype-select">
                    <SelectValue placeholder="Select home type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single Family">Single Family</SelectItem>
                    <SelectItem value="Condo">Condo</SelectItem>
                    <SelectItem value="Townhome">Townhome</SelectItem>
                    <SelectItem value="Multi-Family">Multi-Family</SelectItem>
                    <SelectItem value="Luxury Estate">Luxury Estate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Budget Range */}
              <div className="space-y-2">
                <Label htmlFor="budget">Budget Range</Label>
                <Select
                  value={formData.budget_range}
                  onValueChange={(value) => setFormData({ ...formData, budget_range: value })}
                >
                  <SelectTrigger data-testid="signup-budget-select">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Under $300K">Under $300K</SelectItem>
                    <SelectItem value="$300K - $500K">$300K - $500K</SelectItem>
                    <SelectItem value="$500K - $750K">$500K - $750K</SelectItem>
                    <SelectItem value="$750K - $1M">$750K - $1M</SelectItem>
                    <SelectItem value="$1M - $2M">$1M - $2M</SelectItem>
                    <SelectItem value="Over $2M">Over $2M</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Buying Timeline */}
              <div className="space-y-2">
                <Label htmlFor="timeline">Buying Timeline</Label>
                <Select
                  value={formData.buying_timeline}
                  onValueChange={(value) => setFormData({ ...formData, buying_timeline: value })}
                >
                  <SelectTrigger data-testid="signup-timeline-select">
                    <SelectValue placeholder="Select timeline" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-3 months">0-3 months</SelectItem>
                    <SelectItem value="3-6 months">3-6 months</SelectItem>
                    <SelectItem value="6-12 months">6-12 months</SelectItem>
                    <SelectItem value="12+ months">12+ months</SelectItem>
                    <SelectItem value="Just browsing">Just browsing</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Notes / Wishlist */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes / Wishlist</Label>
                <Textarea
                  id="notes"
                  data-testid="signup-notes-textarea"
                  placeholder="Tell me about your dream home, specific requirements, or any questions you have..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                />
              </div>

              {/* Submit Button */}
              <Button
                data-testid="signup-submit-btn"
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white py-6 text-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
